import { Component } from '@theme/component';

const DOTS_WINDOW = 5;

/**
 * Mobile scroll-snap media carousel for the PDP Hero v2 media grid.
 * Syncs the pagination dots with the active slide and supports
 * dot-click navigation with a sliding 5-dot window.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement} dots
 * @property {HTMLElement} dotsTrack
 *
 * @extends {Component<Refs>}
 */
class PdpMediaCarousel extends Component {
  #total = 0;
  #activeIndex = 0;
  #scrollTicking = false;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  /**
   * Re-measures after the slide set changes — the media grid morphs itself when the
   * shopper picks a colour, which adds and removes slides underneath us.
   */
  refresh() {
    this.refs.track?.scrollTo({ left: 0, behavior: 'instant' });
    this.#activeIndex = 0;
    this.#init();
  }

  /**
   * @param {MouseEvent} event
   */
  handleDotClick(event) {
    const target = /** @type {Element} */ (event.target);
    const dot = target.closest('.pdp-media-grid-v2__dot');
    if (!(dot instanceof HTMLElement)) return;

    const index = parseInt(dot.dataset.index ?? '', 10);
    const track = this.refs.track;
    if (!track) return;

    const slides = track.querySelectorAll('.pdp-media-grid-v2__slide');
    if (slides[index]) {
      slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }

  /* Stable handler references, so re-running #init() after a refresh re-binds the same
     listener on the same node instead of stacking duplicates. */
  #handleDotClickBound = /** @param {Event} event */ (event) =>
    this.handleDotClick(/** @type {MouseEvent} */ (event));

  #handleScrollBound = () => this.#onScroll();

  #init() {
    const track = this.refs.track;
    if (!track) return;

    const slides = track.querySelectorAll('.pdp-media-grid-v2__slide');
    if (slides.length === 0) return;

    this.#total = slides.length;

    const dotsContainer = this.refs.dots;
    if (dotsContainer) {
      dotsContainer.dataset.overflows = this.#total > DOTS_WINDOW ? 'true' : 'false';
      dotsContainer.addEventListener('click', this.#handleDotClickBound);
    }

    this.#updateDots(this.#activeIndex);

    track.addEventListener('scroll', this.#handleScrollBound, { passive: true });
  }

  #onScroll() {
    if (this.#scrollTicking) return;
    this.#scrollTicking = true;

    requestAnimationFrame(() => {
      const track = this.refs.track;
      if (track && track.clientWidth > 0) {
        const index = Math.round(track.scrollLeft / track.clientWidth);
        const clamped = Math.max(0, Math.min(this.#total - 1, index));
        if (clamped !== this.#activeIndex) {
          this.#updateDots(clamped);
        }
      }
      this.#scrollTicking = false;
    });
  }

  /**
   * @param {number} activeIndex
   */
  #updateDots(activeIndex) {
    const dotsTrack = this.refs.dotsTrack;
    if (!dotsTrack) return;

    const dots = dotsTrack.querySelectorAll('.pdp-media-grid-v2__dot');
    if (dots.length === 0) return;

    this.#activeIndex = activeIndex;

    const total = this.#total || dots.length;
    const overflows = total > DOTS_WINDOW;
    const half = Math.floor(DOTS_WINDOW / 2);

    const scrollIndex = overflows
      ? Math.max(0, Math.min(total - DOTS_WINDOW, activeIndex - half))
      : 0;

    dotsTrack.style.setProperty('--dots-scroll-index', String(scrollIndex));

    const trailingEdgeShown = overflows && scrollIndex > 0;
    const leadingEdgeShown = overflows && scrollIndex + DOTS_WINDOW < total;
    const trailingEdgeIndex = scrollIndex;
    const leadingEdgeIndex = scrollIndex + DOTS_WINDOW - 1;

    for (const dot of dots) {
      if (!(dot instanceof HTMLElement)) continue;
      const i = parseInt(dot.dataset.index ?? '', 10);
      dot.classList.toggle('pdp-media-grid-v2__dot--active', i === activeIndex);
      dot.classList.toggle(
        'pdp-media-grid-v2__dot--edge',
        (trailingEdgeShown && i === trailingEdgeIndex) ||
          (leadingEdgeShown && i === leadingEdgeIndex)
      );
    }
  }
}

if (!customElements.get('pdp-media-carousel')) {
  customElements.define('pdp-media-carousel', PdpMediaCarousel);
}
